<template>
  <div class="fixed bottom-8 left-0 right-0 z-10">
    <div 
      v-if="isPreviewOpen && previewPoint"
      class="bg-white rounded-lg shadow-lg z-50 w-[90%] mx-auto max-w-md overflow-hidden focus:outline-none active:outline-none"
      @click="handleAction"
      style="webkit-tap-highlight-color: transparent"
    >
      <ListItemTemplate 
        :title="getTitle()"
        :name="getName()"
        :description="(!activeRoute && !routeError) ? getDescription() : null"
        :image="getImage()"
        :icon="getIcon()"
        :activeRoute="activeRoute"
        :preview="true"
      >
        
        <div v-if="(false && !activeRoute && !locationError)" class="flex justify-between items-center">
          <button
            @click.stop="handleNavigationAction"
            :disabled="calculatingRoute"
            class="py-2 px-2 rounded-full bg-green-600 text-sm text-white hover:bg-green-600 flex items-center focus:outline-none active:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <MapPinIcon class="w-4 h-4 mr-1" />
            <div class="text-xs">Start navigatie</div>
          </button>
        </div>

        <div v-if="routeError" class="text-red-600 text-sm py-1">
          Routeberekening mislukt. Probeer opnieuw.
        </div>

        <div v-if="activeRoute">
          <div class="flex items-center justify-between mb-2">
            <div class="flex-1">
              <div v-if="nextInstruction">{{ nextInstruction.text }}</div>
              <div v-if="distanceToNextTurn !== null && !isNaN(distanceToNextTurn)" class="text-sm text-gray-600">
                {{ distanceToNextTurn < 1000 
                  ? `${Math.round(distanceToNextTurn)}m` 
                  : `${(distanceToNextTurn / 1000).toFixed(1)}km` }}
              </div>
            </div>
          </div>
          
          <div class="flex justify-between items-center">
            <button 
              class="py-2 px-2 rounded-full bg-red-600 text-sm text-white hover:bg-red-700 flex items-center focus:outline-none active:outline-none"
              @click.stop="handleStopNavigation"
            >
              <XMarkIcon class="w-4 h-4 mr-1" />
              <div class="text-xs">Stop navigatie</div>
            </button>
          </div>
        </div>

      </ListItemTemplate>
    </div>
  </div>
</template>

<script>
import { computed } from 'vue'
import { mapGetters, mapActions } from 'vuex'
import ListItemTemplate from './ListItemTemplate.vue'
import { XMarkIcon, InformationCircleIcon } from '@heroicons/vue/24/outline'
import { MapPinIcon } from '@heroicons/vue/24/solid'
import { showMultiLingualText } from '../utils/languageUtils'

export default {
  name: 'VuePointPreview',
  components: {
    ListItemTemplate,
    XMarkIcon,
    InformationCircleIcon,
    MapPinIcon
  },
  props: {
    isPreviewOpen: {
      type: Boolean,
      default: false
    },
    previewPoint: {
      type: Object,
      default: null
    },
    onPreviewAction: {
      type: Function,
      default: null
    },
    onNavigationAction: {
      type: Function,
      default: null
    }
  },
  computed: {
    ...mapGetters('navigation', [
      'activeRoute', 
      'nextInstruction', 
      'distanceToNextTurn', 
      'routeFailure', 
      'calculatingRoute'
    ]),
    ...mapGetters('location', ['isValidLocation']),
    
    routeError() {
      return this.routeFailure
    },
    
    locationError() {
      return !this.isValidLocation
    },
  },
  watch: {
    // Watch for preview data changes
    isPreviewOpen(newVal, oldVal) {
      console.log('=== POINT PREVIEW WATCH ===')
      console.log('isPreviewOpen changed:', { newVal, oldVal })
    },
    previewPoint(newVal, oldVal) {
      console.log('=== POINT PREVIEW DATA WATCH ===')
      console.log('previewPoint changed:', { newVal, oldVal })
      console.log('New preview point data:', newVal)
    }
  },
  methods: {
    ...mapActions('navigation', ['stopNavigation']),
    
    // Helper methods to extract and format text properly
    getTitle() {
      if (!this.previewPoint) return ''
      
      const order = this.previewPoint?.data?.order || this.previewPoint?.order || ''
      const title = this.previewPoint?.data?.title || this.previewPoint?.title
      
      if (typeof title === 'object' && title !== null) {
        return `${order} - ${showMultiLingualText(title, null, false, this.$i18n?.locale || 'en')}`
      }
      return `${order} - ${title || ''}`
    },
    
    getName() {
      if (!this.previewPoint) return ''
      
      const order = this.previewPoint?.data?.order || this.previewPoint?.order || ''
      const name = this.previewPoint?.data?.name || this.previewPoint?.name
      
      if (typeof name === 'object' && name !== null) {
        return `${order} - ${showMultiLingualText(name, null, false, this.$i18n?.locale || 'en')}`
      }
      return `${order} - ${name || ''}`
    },
    
    getDescription() {
      if (!this.previewPoint) return ''
      
      const description = this.previewPoint?.data?.description || this.previewPoint?.description
      
      if (typeof description === 'object' && description !== null) {
        return showMultiLingualText(description, null, false, this.$i18n?.locale || 'en')
      }
      return description || ''
    },
    
    getImage() {
      if (!this.previewPoint) return ''
      
      const image = this.previewPoint?.data?.image?.preview_url || 
                   this.previewPoint?.data?.image || 
                   this.previewPoint?.image
      
      if (typeof image === 'object' && image !== null) {
        return image.preview_url || image.url || ''
      }
      return image || ''
    },
    
    getIcon() {
      if (!this.previewPoint) return ''
      
      const icon = this.previewPoint?.data?.icon?.preview_url || 
                  this.previewPoint?.data?.icon || 
                  this.previewPoint?.icon
      
      if (typeof icon === 'object' && icon !== null) {
        return icon.preview_url || icon.url || ''
      }
      return icon || ''
    },
    
    handleAction() {
      console.log('=== POINT PREVIEW HANDLE ACTION ===')
      console.log('Preview action triggered')
      console.log('Preview point data:', this.previewPoint)
      console.log('onPreviewAction handler:', this.onPreviewAction)
      
      try {
        if (this.onPreviewAction && this.previewPoint) {
          console.log('Calling onPreviewAction with point data')
          this.onPreviewAction(this.previewPoint)
        } else {
          console.warn('No onPreviewAction handler or no preview point data')
        }
        console.log('Emitting close-preview event')
        this.$emit('close-preview')
      } catch (error) {
        console.error('Error in handleAction:', error)
        this.$emit('close-preview')
      }
    },
    
    handleNavigationAction(event) {
      console.log('=== POINT PREVIEW NAVIGATION ACTION ===')
      console.log('Navigation action triggered')
      console.log('Event:', event)
      console.log('Preview point data:', this.previewPoint)
      console.log('onNavigationAction handler:', this.onNavigationAction)
      
      try {
        event.preventDefault()
        event.stopPropagation()
        if (this.onNavigationAction && this.previewPoint) {
          console.log('Calling onNavigationAction with point data')
          this.onNavigationAction(this.previewPoint)
        } else {
          console.warn('No onNavigationAction handler or no preview point data')
        }
      } catch (error) {
        console.error('Error in handleNavigationAction:', error)
      }
    },
    
    handleStopNavigation(event) {
      console.log('=== POINT PREVIEW STOP NAVIGATION ===')
      console.log('Stop navigation action triggered')
      console.log('Event:', event)
      
      try {
        event.preventDefault()
        event.stopPropagation()
        console.log('Calling stopNavigation action')
        this.stopNavigation()
      } catch (error) {
        console.error('Error in handleStopNavigation:', error)
      }
    }
  }
}
</script>

<style scoped>
/* Add any additional styles if needed */
</style> 